<template>
  <b-card
    no-body
    class="mb-1"
  >
    <b-card-header class="d-flex flex-wrap justify-content-between align-items-center">
      <b-card-title class="mb-0">
        Роли и права
        <span
          v-if="data && showName"
          class="text-muted font-weight-normal"
        >— {{ userName(data.user) }}, {{ data.user.email }}</span>
      </b-card-title>
      <b-button
        v-if="data && !editing"
        variant="outline-primary"
        size="sm"
        @click="startAdd"
      >
        <feather-icon icon="PlusIcon" /> Назначить роль
      </b-button>
    </b-card-header>

    <b-card-body>
      <div
        v-if="loading && !data"
        class="text-center"
      >
        <b-spinner small />
      </div>

      <template v-if="data">
        <div
          v-if="!data.assignments.length && !editing"
          class="text-muted mb-1"
        >
          Ролей нет: обычный пользователь сайта, в админку не входит.
        </div>

        <div
          v-for="a in data.assignments"
          :key="a.roleId"
          class="assignment d-flex flex-wrap align-items-center py-50"
        >
          <div class="mr-1">
            <strong>{{ a.name }}</strong>
            <b-badge
              v-if="a.isSystem"
              variant="light-secondary"
              class="ml-50"
            >
              системная
            </b-badge>
            <b-badge
              v-if="!a.active"
              variant="light-warning"
              class="ml-50"
            >
              сейчас не действует
            </b-badge>
            <div
              v-if="a.validFrom || a.validTo"
              class="small text-muted"
            >
              Действует {{ periodText(a.validFrom, a.validTo) }}
            </div>
            <div
              v-if="a.startScoped"
              class="small text-muted"
            >
              {{ START_MODE_TITLES[a.startMode] }}<template v-if="a.startMode !== 'all'">:
                {{ startsText(a) }}</template>
            </div>
          </div>
          <div class="ml-auto">
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="startEdit(a)"
            >
              {{ a.startScoped ? 'Старты и период' : 'Период' }}
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              @click="revoke(a)"
            >
              Снять
            </b-button>
          </div>
        </div>

        <div
          v-if="editing"
          class="editor p-1 my-1"
        >
          <b-form-group
            v-if="!editing.existing"
            label="Роль"
          >
            <b-form-select
              v-model="editing.roleId"
              :options="roleOptions"
            />
          </b-form-group>
          <div
            v-else
            class="mb-1"
          >
            Роль <strong>{{ editing.name }}</strong>
          </div>

          <template v-if="editingStartScoped">
            <b-form-group
              label="Стартовые права роли действуют на"
              class="mb-50"
            >
              <b-form-radio-group
                v-model="editing.startMode"
                :options="startModeOptions"
              />
            </b-form-group>
            <v-select
              v-if="editing.startMode !== 'all'"
              v-model="editing.starts"
              multiple
              label="name"
              :filterable="false"
              :options="startOptions"
              :placeholder="editing.startMode === 'only' ? 'Старты, к которым дать доступ' : 'Старты-исключения'"
              class="mb-1"
              @search="searchStarts"
            >
              <template #option="option">
                #{{ option.id }} {{ option.name }}
              </template>
              <template #no-options>
                Введите название или номер старта
              </template>
            </v-select>
            <div class="small text-muted mb-1">
              Остальные права роли от стартов не зависят. «Все, кроме» открывает и будущие старты; «Только выбранные» — нет.
            </div>
          </template>

          <b-form-group
            label="Действует (пусто — без ограничения)"
            class="mb-1"
          >
            <div class="d-flex flex-wrap align-items-center">
              <span class="mr-50">с</span>
              <b-form-input
                v-model="editing.validFrom"
                type="datetime-local"
                class="period-input mr-1 mb-50"
              />
              <span class="mr-50">до</span>
              <b-form-input
                v-model="editing.validTo"
                type="datetime-local"
                class="period-input mb-50"
              />
            </div>
          </b-form-group>

          <b-button
            variant="primary"
            size="sm"
            class="mr-50"
            :disabled="!editing.roleId || saving"
            @click="saveAssignment"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            Сохранить
          </b-button>
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="editing = null"
          >
            Отмена
          </b-button>
        </div>

        <div
          v-if="data.effective.superadmin || data.effective.permissions.length"
          class="mt-1"
        >
          <div
            class="small text-uppercase text-muted mb-50 effective-toggle"
            @click="showEffective = !showEffective"
          >
            <feather-icon :icon="showEffective ? 'ChevronDownIcon' : 'ChevronRightIcon'" />
            Итоговые права (все роли вместе)
          </div>
          <div v-if="showEffective">
            <div v-if="data.effective.superadmin">
              Все права, включая системные (суперадмин).
            </div>
            <div
              v-for="group in effectiveGroups"
              v-else
              :key="group.group"
              class="mb-50"
            >
              <strong>{{ group.group }}:</strong>&nbsp;
              <span
                v-for="(p, i) in group.permissions"
                :key="p.key"
              >{{ p.title }}<span
                v-if="data.effective.startFilters[p.key]"
                class="text-info"
              > ({{ filterText(data.effective.startFilters[p.key]) }})</span><span v-if="i < group.permissions.length - 1">, </span></span>
            </div>
          </div>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BCardHeader, BCardTitle, BFormGroup, BFormInput, BFormRadioGroup, BFormSelect, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import aclApi from '@/services/acl-api.service'
import {
  byKey, fromLocalInput, loadCatalog, periodText, START_MODE_TITLES, toLocalInput, userName,
} from './catalog'

/**
 * The roles of one user: give, take away, change the starts of a role (all / only / except) — and the effective
 * permissions of all roles together, so nobody has to guess why someone sees something.
 */
export default {
  name: 'AclUserRolesCard',
  components: {
    BBadge, BButton, BCard, BCardBody, BCardHeader, BCardTitle, BFormGroup, BFormInput, BFormRadioGroup, BFormSelect, BSpinner, vSelect,
  },
  props: {
    userId: { type: [Number, String], required: true },
    showName: { type: Boolean, default: false },
  },
  data() {
    return {
      START_MODE_TITLES,
      data: null,
      roles: [],
      catalog: null,
      loading: false,
      saving: false,
      editing: null,
      startOptions: [],
      showEffective: false,
      startModeOptions: Object.entries(START_MODE_TITLES).map(([value, text]) => ({ value, text })),
    }
  },
  computed: {
    roleOptions() {
      const given = new Set(this.data ? this.data.assignments.map(a => a.roleId) : [])
      return [
        { value: null, text: 'Выберите роль' },
        ...this.roles.filter(r => !given.has(r.id)).map(r => ({ value: r.id, text: r.name })),
      ]
    },
    editingStartScoped() {
      if (!this.editing || !this.catalog) return false
      if (this.editing.existing) return true
      const role = this.roles.find(r => r.id === this.editing.roleId)
      if (!role || role.isSystem) return false
      const index = byKey(this.catalog)
      return role.permissions.some(key => index[key] && index[key].startScoped)
    },
    effectiveGroups() {
      if (!this.data || !this.catalog) return []
      const index = byKey(this.catalog)
      const groups = []
      this.data.effective.permissions.forEach(key => {
        const p = index[key]
        if (!p) return
        let group = groups.find(g => g.group === p.group)
        if (!group) {
          group = { group: p.group, permissions: [] }
          groups.push(group)
        }
        group.permissions.push(p)
      })
      return groups
    },
  },
  watch: {
    userId: 'load',
  },
  created() {
    this.load()
  },
  methods: {
    userName,
    periodText,
    async load() {
      this.loading = true
      this.editing = null
      try {
        const [data, roles, catalog] = await Promise.all([
          aclApi.userRoles(this.userId).then(res => res.data),
          aclApi.roles().then(res => res.data),
          loadCatalog(),
        ])
        this.data = data
        this.roles = roles
        this.catalog = catalog
      } finally {
        this.loading = false
      }
    },
    startsText(a) {
      if (!a.startIds.length) return 'ни одного'
      const names = a.starts.map(s => `${s.name} (#${s.id})`)
      const deleted = a.startIds.length - a.starts.length
      return names.join(', ') + (deleted ? `, удалённых стартов: ${deleted}` : '')
    },
    filterText(filter) {
      return `${START_MODE_TITLES[filter.mode].toLowerCase()}: ${filter.startIds.map(id => `#${id}`).join(', ') || '—'}`
    },
    startAdd() {
      this.editing = {
        existing: false, roleId: null, name: '', startMode: 'all', starts: [], validFrom: '', validTo: '',
      }
    },
    startEdit(a) {
      this.editing = {
        existing: true,
        roleId: a.roleId,
        name: a.name,
        startMode: a.startMode,
        starts: a.startIds.map(id => a.starts.find(s => s.id === id) || { id, name: 'старт удалён' }),
        validFrom: toLocalInput(a.validFrom),
        validTo: toLocalInput(a.validTo),
      }
    },
    async searchStarts(q, loading) {
      loading(true)
      try {
        this.startOptions = (await aclApi.searchStarts(q)).data
      } finally {
        loading(false)
      }
    },
    async saveAssignment() {
      const {
        roleId, startMode, starts, validFrom, validTo,
      } = this.editing
      const period = { validFrom: fromLocalInput(validFrom), validTo: fromLocalInput(validTo) }
      const body = this.editingStartScoped ? { startMode, startIds: starts.map(s => s.id), ...period } : period
      this.saving = true
      try {
        this.data = (await aclApi.assignRole(this.userId, roleId, body)).data
        this.editing = null
        this.$emit('changed')
      } catch (e) {
        // the global toast already shows the reason
      } finally {
        this.saving = false
      }
    },
    async revoke(a) {
      const ok = await this.$bvModal.msgBoxConfirm(`Снять роль «${a.name}»?`, {
        title: 'Снять роль', okVariant: 'danger', okTitle: 'Снять', cancelTitle: 'Отмена', centered: true,
      })
      if (!ok) return
      try {
        this.data = (await aclApi.revokeRole(this.userId, a.roleId)).data
        this.$emit('changed')
      } catch (e) {
        // the global toast already shows the reason
      }
    },
  },
}
</script>

<style scoped>
.assignment + .assignment {
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}
.editor {
  border: 1px dashed rgba(34, 41, 47, 0.2);
  border-radius: 0.357rem;
}
.effective-toggle {
  cursor: pointer;
}

.period-input {
  max-width: 220px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
